<template>
  <div class="portal-page consult-board-detail" :class="{ 'consult-board-detail--mall': isMall }">
    <div class="portal-page__inner consult-board-detail__shell">
      <header class="portal-page__head">
        <p v-if="!isMall" class="portal-page__eyebrow">상담</p>
        <h1 class="portal-page__title">{{ isMall ? '문의 상세' : '상담 신청 상세' }}</h1>
      </header>
        <div v-if="loading" class="consult-board-detail__status">불러오는 중...</div>
        <div v-else-if="loadError" class="consult-board-detail__status consult-board-detail__status--error">
          {{ loadError }}
          <router-link :to="listPath" class="btn-secondary consult-board-detail__back-list">
            {{ isMall ? '문의 내역' : '상담 신청 내역' }}
          </router-link>
        </div>

        <article v-else-if="post" class="consult-board-detail__panel">
          <header class="consult-board-detail__head">
            <div class="consult-board-detail__head-bar">
              <span
                class="consult-board-detail__status-badge"
                :class="{
                  'consult-board-detail__status-badge--done': post.status === 'DONE',
                  'consult-board-detail__status-badge--wait': post.status !== 'DONE',
                }"
              >
                {{ post.status === 'DONE' ? '답변완료' : '답변대기' }}
              </span>
              <time class="consult-board-detail__date">{{ formatDate(post.createdAt) }}</time>
            </div>
            <h2 class="consult-board-detail__post-title">{{ post.title }}</h2>
            <dl class="consult-board-detail__meta-strip">
              <div>
                <dt>작성자 ID</dt>
                <dd>{{ post.authorId }}</dd>
              </div>
              <div>
                <dt>글 번호</dt>
                <dd>{{ post.id }}</dd>
              </div>
            </dl>
          </header>

          <dl class="consult-board-detail__info">
            <div>
              <dt>기관명</dt>
              <dd>{{ post.orgName }}</dd>
            </div>
            <div>
              <dt>기관 기호</dt>
              <dd>{{ post.orgCode || '—' }}</dd>
            </div>
            <div>
              <dt>연락처</dt>
              <dd>{{ post.contactPhone }}</dd>
            </div>
            <div>
              <dt>이메일</dt>
              <dd>{{ post.email || '—' }}</dd>
            </div>
            <div>
              <dt>관련 주문</dt>
              <dd>
                <router-link
                  v-if="post.orderCode"
                  :to="`/mall/orders/${encodeURIComponent(post.orderCode)}`"
                >
                  {{ post.orderCode }}
                </router-link>
                <template v-else>{{ isMall ? '주문번호 없음' : '기관 운영 상담' }}</template>
              </dd>
            </div>
          </dl>

          <section class="consult-board-detail__block">
            <h3 class="consult-board-detail__block-title">{{ isMall ? '문의 내용' : '상담 내용' }}</h3>
            <p class="consult-board-detail__content-text">{{ post.content }}</p>
          </section>

          <section v-if="post.hasExcel" class="consult-board-detail__block">
            <h3 class="consult-board-detail__block-title">첨부 파일</h3>
            <div class="consult-board-detail__attach">
              <div class="consult-board-detail__attach-info">
                <AppIcon icon="mdi-file-excel-box" :size="22" />
                <span>{{ post.excelName }}</span>
              </div>
              <button
                type="button"
                class="btn-secondary consult-board-detail__attach-btn"
                :disabled="downloading"
                @click="onDownload"
              >
                {{ downloading ? '다운로드 중...' : 'Excel 다운로드' }}
              </button>
            </div>
          </section>

          <section v-if="responses.length" class="consult-board-detail__block consult-board-detail__reply-block">
            <h3 class="consult-board-detail__block-title">{{ isMall ? '문의 답변' : '상담 답변' }}</h3>
            <article
              v-for="response in responses"
              :key="response.id"
              class="consult-board-detail__reply-card"
            >
              <header class="consult-board-detail__reply-head">
                <strong>{{ response.byName }}</strong>
                <time>{{ formatDate(response.createdAt) }}</time>
              </header>
              <p class="consult-board-detail__reply-text">{{ response.content }}</p>
              <div v-if="response.hasExcel" class="consult-board-detail__attach consult-board-detail__attach--reply">
                <div class="consult-board-detail__attach-info">
                  <AppIcon icon="mdi-file-excel-box" :size="20" />
                  <span>{{ response.excelName }}</span>
                </div>
                <button
                  type="button"
                  class="btn-secondary consult-board-detail__attach-btn"
                  :disabled="downloadingResponseId === response.id"
                  @click="onResponseExcelDownload(response)"
                >
                  {{ downloadingResponseId === response.id ? '다운로드 중...' : 'Excel 다운로드' }}
                </button>
              </div>
            </article>
          </section>

          <section
            v-else-if="post.status === 'WAIT'"
            class="consult-board-detail__block consult-board-detail__reply-wait"
          >
            <h3 class="consult-board-detail__block-title">{{ isMall ? '문의 답변' : '상담 답변' }}</h3>
            <p>답변을 준비 중입니다. 잠시 후 다시 확인해 주세요.</p>
          </section>

          <p class="consult-board-detail__edit-note">
            {{ isMall ? '문의는 수정할 수 없습니다. 내용 변경이 필요하면 삭제 후 다시 작성해 주세요.' : '상담 신청은 수정할 수 없습니다. 내용 변경이 필요하면 삭제 후 다시 신청해 주세요.' }}
          </p>

          <p v-if="verifyError" class="consult-board-detail__error">{{ verifyError }}</p>

          <footer class="consult-board-detail__foot">
            <router-link :to="listPath" class="btn-secondary consult-board-detail__back-list">
              {{ isMall ? '문의 내역' : '상담 신청 내역' }}
            </router-link>
            <button
              type="button"
              class="consult-board-detail__delete"
              :disabled="deleting"
              @click="onDelete"
            >
              {{ deleting ? '삭제 중...' : isMall ? '문의 삭제' : '상담 신청 삭제' }}
            </button>
          </footer>
        </article>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppIcon from '@/components/common/AppIcon.vue'
import {
  deleteConsultPost,
  downloadConsultExcel,
  downloadConsultResponseExcel,
  fetchConsultPostMeta,
  verifyConsultPost,
} from '@/api/consultBoard'
import { MALL_MY_CONSULTS_PATH } from '@/constants/mallMy'
import type {
  ConsultBoardPostDetail,
  ConsultBoardPostMeta,
  ConsultBoardResponseItem,
} from '@/types/consultBoard'

const route = useRoute()
const router = useRouter()
const postId = Number(route.params.id)
const isMall = computed(() => route.meta.layout === 'mall')
const listPath = computed(() => (isMall.value ? MALL_MY_CONSULTS_PATH : '/consult-board'))
const loading = ref(true)
const loadError = ref('')
const meta = ref<ConsultBoardPostMeta | null>(null)

const verifyError = ref('')
const accessToken = ref('')
const post = ref<ConsultBoardPostDetail | null>(null)
const responses = ref<ConsultBoardResponseItem[]>([])
const downloading = ref(false)
const downloadingResponseId = ref<number | null>(null)
const deleting = ref(false)

const loadMeta = async () => {
  loadError.value = ''
  try {
    meta.value = await fetchConsultPostMeta(postId)
  } catch (e) {
    loadError.value = e instanceof Error ? e.message : '글 정보를 불러오지 못했습니다.'
  }
}

const applyVerified = (result: { accessToken: string; post: ConsultBoardPostDetail; responses: ConsultBoardResponseItem[] }) => {
  accessToken.value = result.accessToken
  post.value = result.post
  responses.value = result.responses
}

const tryMemberOpen = async () => {
  const result = await verifyConsultPost(postId)
  applyVerified(result)
}

const onDownload = async () => {
  if (!accessToken.value) return
  downloading.value = true
  try {
    await downloadConsultExcel(postId, accessToken.value)
  } catch (e) {
    verifyError.value = e instanceof Error ? e.message : '다운로드에 실패했습니다.'
  } finally {
    downloading.value = false
  }
}

const onResponseExcelDownload = async (response: ConsultBoardResponseItem) => {
  if (!accessToken.value) return
  downloadingResponseId.value = response.id
  try {
    await downloadConsultResponseExcel(
      postId,
      response.id,
      accessToken.value,
      response.excelName || `consult-response-${response.id}.xlsx`,
    )
  } catch (e) {
    verifyError.value = e instanceof Error ? e.message : '다운로드에 실패했습니다.'
  } finally {
    downloadingResponseId.value = null
  }
}

const onDelete = async () => {
  const targetLabel = isMall.value ? '문의' : '상담 신청'
  if (!window.confirm(`${targetLabel}을 삭제할까요? 삭제 후에는 복구할 수 없습니다.`)) {
    return
  }

  deleting.value = true
  verifyError.value = ''
  try {
    await deleteConsultPost(postId)
    router.push(listPath.value)
  } catch (e) {
    verifyError.value = e instanceof Error ? e.message : '글 삭제에 실패했습니다.'
  } finally {
    deleting.value = false
  }
}

const formatDate = (value: string) => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('ko-KR')
}

onMounted(async () => {
  if (!postId) {
    loadError.value = '잘못된 게시글 번호입니다.'
    loading.value = false
    return
  }
  /* 로그인 회원 본인 글만 연다 */
  try {
    await Promise.all([loadMeta(), tryMemberOpen()])
  } catch (e) {
    loadError.value = e instanceof Error ? e.message : '글을 열 수 없습니다.'
  }
  loading.value = false
})
</script>

<style lang="scss" src="./ConsultBoardDetailPage.scss"></style>
